// react
import { useMemo } from 'react';

// mui
import {
  Box,
  Card,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  Typography,
} from '@mui/material';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Memphis } from '@/components/atoms/Memphis/Memphis';
import { RadioGroupOption, RadioGroupVariant } from './types';
export * from './types';

/**
 * The option-card radio dot: a 22px ring that fills with a 10px Marine Green
 * dot when selected (the prototype's OptionCard pattern).
 */
const RadioDot = ({ checked }: { checked: boolean }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      flexShrink: 0,
      boxSizing: 'border-box',
      border: 2,
      borderColor: checked ? 'primary.main' : 'custom.neutral.300',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {checked && (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: 'primary.main',
        }}
      />
    )}
  </Box>
);

/**
 * RadioGroup lets the user pick one option from a list.
 *
 * - `outlined` (default): option cards — MUI Card per option with the radio
 *   dot; selected cards fill Lime Green with a Marine Green border.
 * - `box-fill`: option cards without the dot; selected cards fill Coral.
 * - `default`: a plain radio-and-label list, no card (used by filters).
 */
export const RadioGroup = ({
  id,
  label,
  description,
  tooltip,
  value,
  onChange,
  isRequired = false,
  isHideTitle = false,
  ariaLabel,
  options,
  isDisabled = false,
  maxColumns = 2,
  variant = 'outlined',
}: RadioGroupProps) => {
  const sortedOptions = useMemo(
    () => options?.slice().sort((a, b) => (a?.order || 0) - (b?.order || 0)),
    [options],
  );

  const isCard = variant !== 'default';

  return (
    <BaseField
      id={id}
      label={label}
      ariaLabel={ariaLabel}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <MuiRadioGroup
        name={id}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        aria-label={ariaLabel}
        aria-labelledby={id}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${maxColumns}, 1fr)`,
            gap: 2,
          }}
        >
          {sortedOptions?.map((option) => {
            const selected = value === option.value;

            const cardVariant =
              variant === 'box-fill' && selected
                ? 'optionFillSelected'
                : selected
                  ? 'optionSelected'
                  : 'option';

            const control = (
              <FormControlLabel
                value={option.value}
                disabled={isDisabled}
                sx={{ m: 0, gap: 1.5, width: '100%' }}
                control={
                  variant === 'box-fill' ? (
                    <Radio sx={{ display: 'none' }} />
                  ) : (
                    <Radio
                      disableRipple
                      icon={<RadioDot checked={false} />}
                      checkedIcon={<RadioDot checked />}
                      sx={{ p: 0 }}
                    />
                  )
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Typography variant={isCard ? 'h5' : 'body1'}>
                      {option.label}
                    </Typography>
                    {option.memphis && (
                      <Memphis size='sm' variant={option.memphis} />
                    )}
                  </Box>
                }
              />
            );

            if (!isCard) {
              return <Box key={option.value}>{control}</Box>;
            }

            return (
              <Card key={option.value} variant={cardVariant}>
                {control}
              </Card>
            );
          })}
        </Box>
      </MuiRadioGroup>
    </BaseField>
  );
};

interface RadioGroupProps extends FieldProps<string> {
  options?: RadioGroupOption[];
  variant?: RadioGroupVariant;
  maxColumns?: number;
}

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
